<template>
  <div
    class="impression"
    :class="[classLayout, { clickable: hasImpressionAction }]"
    :style="styleVarsLayout"
    v-if="props.loading === 'rendered'"
    @click="actionCardItem"
  >
    <div class="impression__content" :style="styleVarsContent">
      <div class="impression__content--header">
        <div class="impression__content--header-title" :style="styleVarsTitle">
          {{ title.text }}
        </div>
        <div class="impression__content--header-sub" :style="styleVarsSubTitle">
          {{ subTitle.text }}
        </div>
      </div>
      <div class="impression__content--body">
        <div class="impression__content--body-value" :style="styleVarsValue">
          {{ impressionValue }}
        </div>
        <div class="impression__content--body-sub" :style="styleVarsUnit">
          {{ unit.text || "\u00A0" }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, onMounted, computed } from "vue";
import mushroom, { Card_item } from "cem-probe-api";
import MethodService from "../../service/MethodService";
import { useUrlPopupStore } from "@/stores/urlPopupStore";
const storeUrlPopup = useUrlPopupStore();
const emit = defineEmits(["showDialogDashboardPopup"]);
const props = defineProps({
  item: Card_item,
  itemData: Object,
  loading: String,
});

const iconSrc = ref(null);
const icon = computed(() => props.item.impression.icon);
const title = computed(() => props.item.impression.title);
const subTitle = computed(() => props.item.impression.sub_title);
const valueImpression = computed(() => props.item.impression.value);
const unit = computed(() => props.item.impression.unit);
const action = computed(() => props.item.impression.action);
const isColumnLayout = computed(() => icon.value?.position?.includes("center"));

const classLayout = computed(() => {
  const pos = icon.value?.position;
  const layoutClass = isColumnLayout.value ? "impression--column" : "";
  return ["impression--pos-" + pos, layoutClass];
});

const styleVarsLayout = computed(() => {
  const currentIcon = icon.value;
  return {
    "--bg-img": `url(${iconSrc.value})`,
    "--bg-size": `${currentIcon?.height}px ${currentIcon?.width}px`,
    "--cursor":
      action.value.link || action.value.popup_id ? "pointer" : "unset",
  };
});

const styleVarsContent = computed(() => {
  const pos = icon.value?.position;

  const vars = {
    "--text-align": "start",
    "--content-display": "unset",
    "--content-align-items": "unset",
    "--content-justify": "unset",
    "--content-height": "unset",
    "--content-direction": "column",
  };

  switch (pos) {
    case "top_left":
    case "middle_left":
    case "bottom_left":
      vars["--text-align"] = "end";
      break;
    case "top_center":
      vars["--text-align"] = "center";
      vars["--content-height"] = "100%";
      vars["--content-display"] = "flex";
      vars["--content-align-items"] = "end";
      vars["--content-justify"] = "space-between";
      vars["--content-direction"] = "column";
      break;
    case "bottom_center":
      vars["--text-align"] = "center";
      vars["--content-height"] = "100%";
      vars["--content-display"] = "flex";
      vars["--content-align-items"] = "start";
      vars["--content-justify"] = "space-between";
      vars["--content-direction"] = "column-reverse";
      break;
    default:
      vars["--text-align"] = "start";
  }

  return vars;
});

const styleVarsTitle = computed(() => ({
  "--title-font-size": `${title.value.fontSize}px`,
  "--title-color": title.value.color,
  "--title-weight": title.value.bold ? "bold" : "normal",
}));

const styleVarsSubTitle = computed(() => ({
  "--subtitle-font-size": `${subTitle.value.fontSize}px`,
  "--subtitle-color": subTitle.value.color,
  "--subtitle-weight": subTitle.value.bold ? "bold" : "normal",
}));

const styleVarsValue = computed(() => {
  const pos = icon.value?.position;
  const dynamicColor = props.itemData?.data?.[0]?.[1]
    ? props.itemData?.data?.[0]?.[1]
    : valueImpression.value.color;

  return {
    "--value-font-size": `${valueImpression.value.fontSize}px`,
    "--value-color": dynamicColor,
    "--value-weight": valueImpression.value.bold ? "bold" : "normal",
    "--value-align": ["top_center", "bottom_center"].includes(pos)
      ? "center"
      : "unset",
  };
});

const styleVarsUnit = computed(() => ({
  "--unit-font-size": `${unit.value.fontSize}px`,
  "--unit-color": unit.value.color,
  "--unit-weight": unit.value.bold ? "bold" : "normal",
}));

const hasImpressionAction = computed(() => {
  return !!(action.value.link || action.value.popup_id);
});

const getFileIdIcon = async (idIcon) => {
  if (!idIcon) return null;
  try {
    const response = await mushroom.card_item_icon.findByIdAsync({
      id: idIcon,
    });
    return response.result?.file_id;
  } catch (e) {
    console.error("Lỗi tải icon:", e);
    return null;
  }
};

const renderIconSrc = (fileId) => {
  return fileId ? mushroom.$file.linkBuilder.thumb.id(fileId).build() : null;
};

const impressionValue = computed(() => {
  const value = props.itemData?.data?.[0]?.[0];
  const isInvalidValue =
    value == null || value === "" || value === "NA" || value === "-";

  if (isInvalidValue) return "-";
  return value;
});

const actionCardItem = () => {
  if (!action.value || Object.keys(action).length === 0) return;

  const impression = impressionValue.value;

  const hasLink = !!action.value.link;
  const hasPopup = !!action.value.popup_id;

  if (!hasLink && !hasPopup) return;

  if (hasLink) {
    const finalLink = MethodService.renderLinkChart(
      {
        value: impression,
      },
      action.value.link
    );
    window.open(finalLink);
  } else if (hasPopup) {
    const chartUrl = MethodService.renderLinkChart(
      {
        value: impression,
      },
      null
    );
    storeUrlPopup.initUrlPopup();
    storeUrlPopup.pushUrlPopup(chartUrl);
    emit("showDialogDashboardPopup", action.value.popup_id, chartUrl);
  }
};

onMounted(async () => {
  const idIcon = icon.value?.icon_id;
  const fileId = await getFileIdIcon(idIcon);
  iconSrc.value = renderIconSrc(fileId);
});
</script>

<style lang="scss">
.impression {
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin: -12px -24px;
  width: calc(100% + 2 * 24px);
  padding: 12px 24px;
  background-image: var(--bg-img);
  background-repeat: no-repeat;
  background-size: var(--bg-size, auto);
  background-position: center;
  cursor: var(--cursor, unset);

  &__content {
    width: 100%;
    display: var(--content-display, flex);
    flex-direction: var(--content-direction, column);
    justify-content: var(--content-justify, space-between);
    align-items: var(--content-align-items, stretch);
    height: var(--content-height, auto);
    text-align: var(--text-align, start);
    &--header-title {
      font-size: var(--title-font-size);
      color: var(--title-color);
      font-weight: var(--title-weight);
    }
    &--header-sub {
      font-size: var(--subtitle-font-size);
      color: var(--subtitle-color);
      font-weight: var(--subtitle-weight);
    }
    &--body {
      width: 100%;
    }
    &--body-value {
      width: inherit;
      font-size: var(--value-font-size);
      color: var(--value-color);
      font-weight: var(--value-weight);
      text-align: var(--value-align);
      line-height: 1;
    }
    &--body-sub {
      width: inherit;
      font-size: var(--unit-font-size);
      color: var(--unit-color);
      font-weight: var(--unit-weight);
      margin-top: 11px;
      line-height: 1;
    }
  }

  &--column {
    flex-direction: column;
    align-items: center;
  }

  &--pos-top_right {
    background-position: right top;
  }

  &--pos-top_left {
    background-position: left top;
  }

  &--pos-bottom_left {
    background-position: left bottom;
  }

  &--pos-bottom_right {
    background-position: right bottom;
  }

  &--pos-middle_left {
    background-position: left center;
  }

  &--pos-middle_right {
    background-position: right center;
  }

  &--pos-top_center {
    background-position: center top;
  }

  &--pos-bottom_center {
    background-position: center bottom;
  }

  &__icon {
    position: absolute;
  }
}
</style>
